import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/shared/lib/utils";
import { Link, useLocation } from "@tanstack/react-router";
import { LogIn, Menu, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { HCA_PHONE_DISPLAY, HCA_PHONE_TEL, usePublicActions } from "./public-actions";

const NAV_LINKS = [
  { to: "/", label: "Úvod" },
  { to: "/o-nas", label: "O nás" },
  { to: "/nas-tym", label: "Náš tým" },
  { to: "/terminy", label: "Termíny" },
] as const;

/** Public sticky header s brand logo + horizontal nav + mobile sheet. */
export function HcaPublicHeader() {
  const location = useLocation();
  const { openDiagnoza, openKontakt } = usePublicActions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const updateScrolled = () => setScrolled(window.scrollY > 24);
    updateScrolled();
    window.addEventListener("scroll", updateScrolled, { passive: true });
    return () => window.removeEventListener("scroll", updateScrolled);
  }, []);

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);
  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled && !mobileOpen;

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-40 border-b transition-colors duration-300",
        transparent
          ? "border-transparent bg-transparent text-[color:var(--hca-ivory)]"
          : "border-border bg-background/95 text-foreground shadow-sm backdrop-blur",
      )}
      data-testid="hca-public-header"
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-3" data-testid="hca-public-header-brand">
          <img
            src={
              transparent
                ? "/branding/HCA_Moravia_logo_navy.jpg"
                : "/branding/HCA_Moravia_logo_ivory.jpg"
            }
            alt="HCA Moravia"
            className="h-12 w-auto rounded-md object-contain"
          />
          <span
            className={cn(
              "hidden text-[0.7rem] leading-tight sm:inline",
              transparent ? "text-[color:var(--hca-ivory)]/75" : "text-muted-foreground",
            )}
          >
            Pro majitele
            <br />a vedení firem
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="Hlavní navigace">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={cn(
                "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive(link.to)
                  ? transparent
                    ? "bg-white/15 text-white"
                    : "bg-primary/10 text-primary"
                  : transparent
                    ? "text-white/86 hover:bg-white/10 hover:text-white"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
              data-testid={`hca-public-header-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "ml-1 h-9",
              transparent
                ? "border-white/35 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                : "border-transparent bg-transparent text-foreground hover:bg-muted hover:text-foreground",
            )}
            onClick={openDiagnoza}
            data-testid="hca-public-header-diagnoza-button"
          >
            Diagnostika
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(
              "h-9",
              transparent
                ? "border-white/35 bg-white/10 text-white hover:bg-white/20 hover:text-white"
                : "border-transparent bg-transparent text-foreground hover:bg-muted hover:text-foreground",
            )}
            onClick={() => openKontakt()}
            data-testid="hca-public-header-kontakt-button"
          >
            Kontakt
          </Button>
          <a
            href={`tel:${HCA_PHONE_TEL}`}
            className={cn(
              "ml-2 hidden items-center gap-2 rounded-md px-2.5 py-2 text-sm font-semibold transition-colors lg:flex",
              transparent ? "text-white hover:bg-white/10" : "text-foreground hover:bg-muted",
            )}
            data-testid="hca-public-header-phone"
          >
            <Phone className="h-4 w-4 text-primary" aria-hidden="true" />
            {HCA_PHONE_DISPLAY}
          </a>
          <Button size="sm" className="ml-2" asChild data-testid="hca-public-header-login-button">
            <Link to="/login" data-testid="hca-public-header-login-link">
              <LogIn className="h-4 w-4" aria-hidden="true" />
              Klientská zóna
            </Link>
          </Button>
        </nav>

        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "md:hidden",
                transparent
                  ? "text-white hover:bg-white/10 hover:text-white"
                  : "text-foreground hover:bg-muted",
              )}
              aria-label="Otevřít menu"
              data-testid="hca-public-header-mobile-trigger"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="flex w-80 flex-col sm:w-96">
            <SheetHeader>
              <SheetTitle>Navigace</SheetTitle>
            </SheetHeader>
            <nav className="mt-6 flex flex-1 flex-col" aria-label="Mobilní navigace">
              <div className="flex flex-1 flex-col gap-1">
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive(link.to)
                        ? "bg-primary/10 text-primary"
                        : "text-foreground hover:bg-muted",
                    )}
                    data-testid={`hca-public-header-mobile-link-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openDiagnoza();
                  }}
                  className="cursor-pointer rounded-md px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted"
                  data-testid="hca-public-header-mobile-diagnoza"
                >
                  Spustit diagnostiku
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    openKontakt();
                  }}
                  className="cursor-pointer rounded-md px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted"
                  data-testid="hca-public-header-mobile-kontakt"
                >
                  Kontakt
                </button>
              </div>

              <div className="mt-auto flex flex-col gap-2 border-t border-border pt-4">
                <a
                  href={`tel:${HCA_PHONE_TEL}`}
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 rounded-md bg-[color:var(--hca-navy)] px-3 py-2 text-sm font-semibold text-[color:var(--hca-ivory)]"
                  data-testid="hca-public-header-mobile-phone"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  {HCA_PHONE_DISPLAY}
                </a>
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                  data-testid="hca-public-header-mobile-login"
                >
                  Klientská zóna
                </Link>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
